import { getPostPlainBody } from "@/entities/post";
import { getThreadOp } from "@/entities/thread/utils";
import {
  CATALOG_HOVER_SCALE_MAX,
  CATALOG_HOVER_SCALE_MIN,
} from "@/features/catalog/stores/catalogStore";
import type {
  AnchorRect,
  Size,
} from "@/features/catalog/utils/actionPopoverPosition";
import { decodeHtmlEntities } from "@/shared/lib/htmlToPlainText";

/** 拡大率100%のときのスレ画表示幅(px)。 */
export const CATALOG_HOVER_BASE_IMAGE_WIDTH = 180;

interface CatalogHoverPosition {
  left: number;
  top: number;
}

export interface CatalogHoverLayout {
  image: CatalogHoverPosition;
  body: CatalogHoverPosition | null;
}

interface CatalogHoverLayoutOptions {
  /** 拡大画像と本文カードの間隔(px)。既定5px。 */
  bodyGap?: number;
  /** ビューポート端からの最小マージン(px)。既定12px。 */
  edgeGap?: number;
}

interface CatalogHoverImageMetrics {
  complete: boolean;
  naturalWidth: number;
  naturalHeight: number;
  offsetWidth: number;
  offsetHeight: number;
}

const DEFAULT_BODY_GAP = 5;
const DEFAULT_EDGE_GAP = 12;

/**
 * 拡大率(%)からスレ画の表示幅(px)を求める。
 * ストア側(catalogStore)でも範囲を丸めているが、想定外の呼び出し元から
 * 0/負数/範囲外の値が渡っても幅が0pxや負値にならないよう、ここでも丸める。
 */
export function resolveCatalogHoverImageWidth(scalePercent: number): number {
  if (!Number.isFinite(scalePercent)) return CATALOG_HOVER_BASE_IMAGE_WIDTH;
  const clampedScale = Math.max(
    CATALOG_HOVER_SCALE_MIN,
    Math.min(CATALOG_HOVER_SCALE_MAX, scalePercent),
  );
  return Math.round((CATALOG_HOVER_BASE_IMAGE_WIDTH * clampedScale) / 100);
}

/** 読み込みとレイアウトが完了した画像だけを配置計算へ渡す。 */
export function resolveCatalogHoverImageSize(
  metrics: CatalogHoverImageMetrics,
): Size | null {
  if (
    !metrics.complete ||
    !isPositiveFinite(metrics.naturalWidth) ||
    !isPositiveFinite(metrics.naturalHeight) ||
    !isPositiveFinite(metrics.offsetWidth) ||
    !isPositiveFinite(metrics.offsetHeight)
  ) {
    return null;
  }
  return { width: metrics.offsetWidth, height: metrics.offsetHeight };
}

/**
 * 元サムネイル・拡大画像・本文カードの寸法から、それぞれの表示位置を求める。
 *
 * 拡大画像の中心を元サムネイルの中心へ重ねる。
 * 画像は画面外へ出ても移動・縮小せず、本文カードだけをビューポート内へ
 * 丸める(clamp)。画像と本文を別座標にすることで、画像の拡大位置を保ったまま
 * 長い本文へアクセスできるようにする。
 */
export function computeCatalogHoverLayout(
  anchorRect: AnchorRect,
  imageSize: Size,
  bodySize: Size | null,
  viewport: Size,
  options?: CatalogHoverLayoutOptions,
): CatalogHoverLayout {
  const bodyGap = options?.bodyGap ?? DEFAULT_BODY_GAP;
  const edgeGap = options?.edgeGap ?? DEFAULT_EDGE_GAP;
  const anchorWidth = anchorRect.right - anchorRect.left;
  const anchorHeight = anchorRect.bottom - anchorRect.top;
  const image = {
    left: anchorRect.left + (anchorWidth - imageSize.width) / 2,
    top: anchorRect.top + (anchorHeight - imageSize.height) / 2,
  };

  return {
    image,
    body: bodySize
      ? {
          left: clamp(image.left, edgeGap, viewport.width, bodySize.width),
          top: clamp(
            image.top + imageSize.height + bodyGap,
            edgeGap,
            viewport.height,
            bodySize.height,
          ),
        }
      : null,
  };
}

/**
 * ホバープレビューに出すOP本文。カタログカードのタイトル(getThreadTitle)と違い、
 * 表示長で切り詰めない(ポップアップ側でスクロールさせる)。
 */
export function getCatalogHoverBody(
  thread: Parameters<typeof getThreadOp>[0],
): string {
  const op = getThreadOp(thread);
  const body = op ? getPostPlainBody(op).trim() : "";
  return decodeHtmlEntities(body);
}

/** [edgeGap, viewportExtent - size - edgeGap] の範囲へ値を丸める。範囲が逆転する場合はedgeGapへ丸める。 */
function clamp(
  value: number,
  edgeGap: number,
  viewportExtent: number,
  size: number,
): number {
  const max = Math.max(edgeGap, viewportExtent - size - edgeGap);
  return Math.min(Math.max(value, edgeGap), max);
}

function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}
